/*
 * Everything the browser knows about GitHub (and now Supabase) arrives
 * through these calls. The session cookie is HttpOnly, so there is no token
 * to attach here and none to leak: credentials: 'same-origin' is the whole
 * auth story client-side.
 */
import {
  ApiError,
  type DatabasesResponse,
  type Me,
  type OrgHealth,
  type RepoHealth,
  type RepoState,
  type RepoSummary
} from '../types'

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    throw new ApiError(res.status, body?.error || `Request failed (${res.status})`, !!body?.unauthenticated)
  }
  return body as T
}

export const getMe = () => get<Me>('/api/me')

export const getRepos = () => get<{ org: string; count: number; repos: RepoSummary[] }>('/api/repos')

export const syncRepo = (repo: string) => get<RepoState>(`/api/sync?repo=${encodeURIComponent(repo)}`)

export const getDatabases = () => get<DatabasesResponse>('/api/databases')

/* Health is a separate call from the sync on purpose: the rail should paint
 * from GitHub data immediately, with the badges resolving a beat later, rather
 * than holding the whole page behind however long a dead host takes to time
 * out. */
export const getRepoHealth = (repo: string) => get<RepoHealth>(`/api/health?repo=${encodeURIComponent(repo)}`)

export const getOrgHealth = () => get<OrgHealth>('/api/health?scope=org')

export const loginUrl = (returnTo = window.location.pathname + window.location.search) =>
  `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
}
